// DLC_02 · Zlecenia wydruku (print orders) — TanStack Query hooks.
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import * as api from "./api";

const qk = {
  root: ["paper-send", "print-orders"] as const,
  list: (p?: Record<string, string>) => ["paper-send", "print-orders", p ?? {}] as const,
  pendingCount: ["paper-send", "print-orders", "pending-count"] as const,
};

export const usePrintOrders = (params?: Record<string, string>) =>
  useQuery({ queryKey: qk.list(params), queryFn: () => api.getPrintOrders(params) });

export const usePrintOrderPendingCount = (enabled = true) =>
  useQuery({
    queryKey: qk.pendingCount,
    queryFn: () => api.getPrintOrdersPendingCount(),
    enabled,
  });

// Invalidation clears all print-order caches regardless of the active list filter.
export const usePrintOrderMutations = () => {
  const qc = useQueryClient();
  const inv = () => qc.invalidateQueries({ queryKey: qk.root });
  return {
    create: useMutation({ mutationFn: (fd: FormData) => api.createPrintOrder(fd), onSuccess: inv }),
    update: useMutation({
      mutationFn: (v: { id: string; fd: FormData }) => api.updatePrintOrder(v.id, v.fd),
      onSuccess: inv,
    }),
    remove: useMutation({ mutationFn: (id: string) => api.deletePrintOrder(id), onSuccess: inv }),
    accept: useMutation({ mutationFn: (id: string) => api.acceptPrintOrder(id), onSuccess: inv }),
    reject: useMutation({
      mutationFn: (v: { id: string; note: string }) => api.rejectPrintOrder(v.id, v.note),
      onSuccess: inv,
    }),
    cancel: useMutation({ mutationFn: (id: string) => api.cancelPrintOrder(id), onSuccess: inv }),
    markPrinted: useMutation({ mutationFn: (id: string) => api.markPrintOrderPrinted(id), onSuccess: inv }),
    markSent: useMutation({ mutationFn: (id: string) => api.markPrintOrderSent(id), onSuccess: inv }),
    undoPrinted: useMutation({ mutationFn: (id: string) => api.undoPrintOrderPrinted(id), onSuccess: inv }),
    // Nr listu — dopisywany po oznaczeniu wysyłki (i poprawialny później).
    setTrackingNumber: useMutation({
      mutationFn: (v: { id: string; trackingNumber: string }) =>
        api.setPrintOrderTrackingNumber(v.id, v.trackingNumber),
      onSuccess: inv,
    }),
    setShipping: useMutation({
      mutationFn: (v: { id: string; needsShipping: boolean }) => api.setPrintOrderShipping(v.id, v.needsShipping),
      onSuccess: inv,
    }),
    // Zwrotek pod jednym zleceniem bywa kilka — wysyłamy listę plików; bez mode="replace"
    // backend dokleja je do już wgranego dokumentu.
    uploadReceipt: useMutation({
      mutationFn: (v: { id: string; files: File[]; mode?: "append" | "replace" }) =>
        api.uploadPrintOrderReceiptConfirmation(v.id, v.files, v.mode),
      onSuccess: inv,
    }),
    markReturned: useMutation({
      mutationFn: (v: { id: string; files?: File[] | null }) =>
        api.markPrintOrderReturned(v.id, v.files),
      onSuccess: inv,
    }),
    uploadReturnAttachment: useMutation({
      mutationFn: (v: { id: string; files: File[]; mode?: "append" | "replace" }) =>
        api.uploadPrintOrderReturnAttachment(v.id, v.files, v.mode),
      onSuccess: inv,
    }),
  };
};
